import { useState } from 'react'

import { Option } from '@joor/design-system'
import { FormikErrors, FormikProps, useFormik } from 'formik'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import {
  BillingAddress,
  Geography,
  OrderBilling,
  Tax,
} from '__generated__/atlas-types'

import { formatNameWithCode } from 'utils/formatters'

import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import {
  BillingAddressFormValues,
  EMPTY_FORM_STATE,
  FieldIds,
} from '../../Forms/BillingAddressForm/billingAddressForm.types'
import {
  formatBillingAddressToFormValues,
  formatOrderBillingToFormValues,
  formatRetailerLocationToFormValues,
  getInitialBillingFormValues,
} from '../../Forms/BillingAddressForm/billingAddressForm.utils'
import {
  DEFAULT_ORDER_ADDRESS_ID,
  REQUIRED_FIELD_ERROR_MESSAGE,
  extractCompanyFromLabel,
  getCompanyNameLabel,
  getCountryOption,
} from '../../Forms/forms.utils'
import { RetailerInfo } from './useAddressModalData'
import { messages } from 'features/Orders/Review/components/Overview/components/AddressesWidget/constants/messages'
import { isSameBillingAddressThanValues } from 'features/Orders/Review/utils/addresses.utils'
import { getDropdownCallbacks } from 'features/Orders/Review/utils/formik.utils'

const phoneRegExp = new RegExp(/^\+?[\d\s\-()]*$/)

const BillingFormValidationSchema = Yup.object().shape({
  [FieldIds.AddressOption]: Yup.object()
    .required(REQUIRED_FIELD_ERROR_MESSAGE)
    .nullable(),
  [FieldIds.CompanyName]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.Address1]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.City]: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  [FieldIds.Phone]: Yup.string().matches(phoneRegExp, 'Not valid phone number'),
  [FieldIds.Territory]: Yup.string().max(
    40,
    'Input cannot exceed 40 characters',
  ),
})

type UseBillingFormParams = {
  orderBilling?: OrderBilling
  countryOptions?: Option[]
  orderTax?: Tax | null
  aggregatedOptions?: Option[]
  paymentMethod?: {
    id: string
    name?: string | null
    code?: string | null
  } | null
  billingAddresses?: BillingAddress[]
  countries?: Pick<Geography, 'name' | 'id' | 'taxLabel'>[]
  retailerInfo?: RetailerInfo
}

export type BillingFormikErrors = FormikErrors<BillingAddressFormValues>
export type HandleBillingChange =
  FormikProps<BillingAddressFormValues>['handleChange']

export function useBillingForm({
  orderBilling,
  orderTax,
  paymentMethod,
  countryOptions = [],
  billingAddresses = [],
  aggregatedOptions = [],
  countries = [],
  retailerInfo,
}: UseBillingFormParams) {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const [currentTaxLabel, setCurrentTaxLabel] = useState<string | null>(
    orderTax?.label as string | null,
  )
  const [disableEditCompany, setDisableEditCompany] =
    useState(!isAccountTypeBrand)
  const [csdSelectedAddress, setCsdSelectedAddress] = useState<BillingAddress>()
  const [isPrimaryLocationSet, setIsPrimaryLocationSet] = useState(false)
  const [storeNameLabel, setStoreNameLabel] = useState<string>(
    messages.storeName,
  )

  const commonValues = {
    taxNumber: orderTax?.number,
  }

  const initialValues = {
    ...getInitialBillingFormValues({
      ...commonValues,
      orderBilling,
      countryOptions,
      billingAddressOptions: aggregatedOptions,
    }),
    paymentMethod: paymentMethod
      ? ({
          value: paymentMethod?.id,
          label: formatNameWithCode(paymentMethod?.name, paymentMethod?.code),
        } as Option)
      : null,
  }

  const formik = useFormik<BillingAddressFormValues>({
    onSubmit: () => {},
    initialValues,
    validationSchema: BillingFormValidationSchema,
  })

  const handleSelectAddress = ({ selected }: { selected: Option }) => {
    const isSelectedPrimaryLocation =
      selected.value === retailerInfo?.primaryLocation?.id
    setIsPrimaryLocationSet(isSelectedPrimaryLocation)
    setCsdSelectedAddress(undefined)

    const isSelectedRetailerLocation =
      retailerInfo?.locations?.some(
        (location) => location.id === selected.value,
      ) && !isSelectedPrimaryLocation

    const isSelectedOrderBillingAddress =
      selected.value === DEFAULT_ORDER_ADDRESS_ID

    const notAddressDependentValues = {
      ...commonValues,
      selected: { ...selected, label: extractCompanyFromLabel(selected.label) },
      paymentMethodOption: formik.values[FieldIds.PaymentMethod],
    }

    if (isSelectedPrimaryLocation) {
      const countryOption = getCountryOption(
        retailerInfo?.primaryLocation?.country,
        countryOptions,
      )

      formik.setValues(
        formatRetailerLocationToFormValues({
          ...notAddressDependentValues,
          retailerLocation: retailerInfo?.primaryLocation,
          countryOption,
        }),
      )
      setStoreNameLabel(
        getCompanyNameLabel(retailerInfo?.primaryLocation?.type),
      )
      handleTaxLabelVisibility(countryOption)
      setDisableEditCompany(isAccountTypeBrand)
      return
    }

    if (isSelectedRetailerLocation) {
      const foundRetailerLocation = retailerInfo?.locations?.find(
        (location) => location.id === selected.value,
      )
      if (foundRetailerLocation) {
        formik.setValues(
          formatRetailerLocationToFormValues({
            ...notAddressDependentValues,
            retailerLocation: foundRetailerLocation,
            countryOption: getCountryOption(
              foundRetailerLocation?.country,
              countryOptions,
            ),
          }),
        )
        setStoreNameLabel(
          getCompanyNameLabel(retailerInfo?.primaryLocation?.type),
        )
      } else {
        // I can't imagine when this could happen, but just in case
        formik.setValues({
          ...EMPTY_FORM_STATE,
          [FieldIds.AddressOption]: selected,
        })
      }
      setDisableEditCompany(false)
      return
    }

    setStoreNameLabel(messages.storeName)
    setDisableEditCompany(!isAccountTypeBrand)

    if (isSelectedOrderBillingAddress) {
      const countryOption = getCountryOption(
        orderBilling?.address?.country,
        countryOptions,
      )

      formik.setValues(
        formatOrderBillingToFormValues({
          ...notAddressDependentValues,
          orderBilling,
          countryOption,
        }),
      )

      handleTaxLabelVisibility(countryOption)
      return
    }

    const billingAddress = billingAddresses.find(
      (address) => address.id === selected.value,
    )

    if (isAccountTypeBrand && billingAddress) {
      setCsdSelectedAddress(billingAddress)
    }

    const countryOption = getCountryOption(
      billingAddress?.address?.country,
      countryOptions,
    )

    formik.setValues(
      formatBillingAddressToFormValues({
        ...notAddressDependentValues,
        billingAddress,
        countryOption,
      }),
    )

    handleTaxLabelVisibility(countryOption)
  }

  const handleTaxLabelVisibility = (countryOption: Option | undefined) => {
    const taxLabel =
      countries.find((country) => countryOption?.value === country?.id)
        ?.taxLabel ?? null
    setCurrentTaxLabel(taxLabel)
  }

  const handleCountryChange = ({ selected }: { selected: Option }) => {
    const orderCountry = orderBilling?.address?.country

    orderCountry === selected.label && orderTax?.number
      ? formik.setFieldValue(FieldIds.TaxNumber, orderTax.number)
      : formik.setFieldValue(FieldIds.TaxNumber, '')

    formik.setFieldValue(FieldIds.Country, selected)

    handleTaxLabelVisibility(selected)
  }

  const handleClearCountry = () => {
    formik.setFieldValue(FieldIds.Country, '')
  }

  const countryDropdownHandlers = {
    onSelect: handleCountryChange,
    onClear: handleClearCountry,
  }

  const paymentMethodDropdownHandlers = getDropdownCallbacks(
    FieldIds.PaymentMethod,
    formik,
    false,
  )

  const resetBillingForm = async () => {
    const values = {
      ...EMPTY_FORM_STATE,
      addressOption: formik.values[FieldIds.AddressOption],
    }
    formik.resetForm({
      values,
    })

    await formik.validateForm(values)
  }

  const hasBillingCSDMismatch =
    !!csdSelectedAddress &&
    !isSameBillingAddressThanValues(csdSelectedAddress, formik.values)

  return {
    billingValues: formik.values,
    billingErrors: formik.errors,
    billingTaxLabel: currentTaxLabel,
    isValidBillingForm:
      formik.isValid && Boolean(formik.values[FieldIds.AddressOption]),
    isBillingFormTouched: formik.dirty,
    disableBillingCompanyField: disableEditCompany,
    handleSelectBillingAddress: handleSelectAddress,
    billingCountryHandlers: countryDropdownHandlers,
    billingPaymentMethodHandlers: paymentMethodDropdownHandlers,
    handleBillingChange: formik.handleChange,
    resetBillingForm,
    selectedCSDBillingAddress: csdSelectedAddress,
    isPrimaryLocBillingSet: isPrimaryLocationSet,
    hasBillingCSDMismatch,
    billingNameLabel: storeNameLabel,
  }
}
